import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { CrewSwitcher } from "@/components/crew-switcher"
import { useCurrentContext } from "@/hooks/use-current-context"

import {
  WizardProgress,
  type WizardProgressProps,
  type WizardStepKey,
} from "./wizard-progress"

export interface WizardShellBrand {
  id?: number
  name: string
  logo_url: string | null
}

export interface WizardShellProps {
  /**
   * The brand whose wizard is being completed. Surfaces in the slim
   * top-bar (logo + name) — Crew is NOT white-label, so the brand
   * appears as DATA (not theme tokens).
   */
  brand: WizardShellBrand
  /**
   * The active step key. Drives the `data-step` discriminator on the
   * shell root + the WizardProgress active state.
   */
  step: WizardStepKey | "welcome"
  /**
   * Server-computed required steps for the active brand. 3 on first-brand,
   * 2 on second-brand (TikTok skipped server-side). Empty on Welcome.
   */
  required_steps: WizardProgressProps["required_steps"]
  /** Step content rendered inside the centered <main>. */
  children: ReactNode
  /**
   * OKS-304 — Optional full-bleed slot rendered between the sticky
   * header and the centered `<main>`. Used by `Onboarding/Welcome.tsx`
   * to surface the brand cover band (3:1 hero) per UX Spec §2.3 / PRD
   * R9 — Welcome step ONLY; Tiktok / Address / Charter pass nothing.
   * Span = full content-area width (NOT capped at `max-w-md`); the
   * caller decides the chrome (rounded corners, etc.) via the band's
   * own `className`.
   */
  cover?: ReactNode
  className?: string
}

/**
 * WizardShell — branded chrome wrapper for every step of the OKS-24
 * onboarding wizard.
 *
 * Layout (UX Spec §3):
 *   - Sticky top-bar carrying brand logo + brand name + WizardProgress + the
 *     `<CrewSwitcher>` chip (rendered when ≥2 memberships).
 *   - Centered <main role="main" tabIndex={-1}> for step content.
 *
 * **Tab-order (UX Spec §3 + §7):** the switcher chip is visually top-right
 * but DOM-ordered AFTER step content + primary CTA — keyboard users complete
 * the step before the chip becomes reachable. We achieve this with
 * `flex-row-reverse` on the chip cluster (visual position right, DOM order
 * last). No `aria-flowto` needed.
 *
 * **Switcher visibility** (OKS-157): the shell co-gates the slot wrapper on
 * the SAME condition as `<CrewSwitcher>`'s own gate —
 * `currentUser.role === "creator" && availableBrands.length >= 2`. A
 * single-crew creator has nothing to switch to, so the chip self-renders to
 * nothing; previously the shell still emitted an empty `fixed` wrapper for
 * them. Sharing one condition keeps the wrapper out of the DOM unless the
 * chip will actually render. The wrapper is `fixed`-positioned, so it never
 * participated in document flow — gating it changes no layout.
 *
 * **Welcome step:** pass `step="welcome"` and `required_steps={[]}` — the
 * progress indicator hides, the data-step discriminator becomes "welcome"
 * for QA addressability, the chrome stays the same.
 */
export function WizardShell({
  brand,
  step,
  required_steps,
  children,
  cover,
  className,
}: WizardShellProps) {
  const { t } = useTranslation()
  const { currentUser, availableBrands } = useCurrentContext()
  const showSwitcher =
    currentUser?.role === "creator" && availableBrands.length >= 2
  // Index used for `data-step-index` (1-based), 0 on Welcome (post-step).
  const stepIndex = (() => {
    if (step === "welcome") return 0
    const idx = required_steps.indexOf(step)
    return idx < 0 ? 0 : idx + 1
  })()

  return (
    <div
      data-testid="wizard-shell"
      data-step={step}
      data-step-index={stepIndex}
      className={[
        "bg-background flex min-h-svh flex-col",
        className ?? "",
      ].join(" ")}
    >
      <header
        data-testid="wizard-header"
        // Sticky top-bar per UX Spec §3 — keeps brand chrome + step
        // indicator on screen as the body scrolls (matters on Charter's
        // scrollable rich-text region + Address mobile). z-20 sits above
        // the body but BELOW the fixed switcher chip (z-30) — the chip
        // floats in the header's right-corner negative space and must
        // win the z-stack so the opaque `bg-background` doesn't occlude
        // it (B7 manual-check regression — multi-brand creator).
        className="bg-background border-border/40 sticky top-0 z-20 border-b"
      >
        <div className="flex items-center gap-2 px-6 pt-4 pb-2">
          {/*
            OKS-303 — `<BrandLogo>` (TRD KD-2) renders the `<img>` AND the
            `BrandInitialMonogram` fallback (`onError` swap, UX Spec §3).
            Shares the canonical `onboarding-shell-brand-logo` testid with
            `<OnboardingShell>` (TRD KD-3) so a single Playwright selector
            covers all 5 onboarding step surfaces. `text-xs` keeps the
            ~4:1 ratio at the 28px tile (OKS-266 pairing).
          */}
          <BrandLogo
            logoUrl={brand.logo_url}
            name={brand.name}
            className="size-7 rounded-md"
            monogramClassName="text-xs"
            alt={t("wizard.chrome.brand_logo_alt", { brand: brand.name })}
            testId="onboarding-shell-brand-logo"
          />

          <span
            className="text-foreground text-sm font-medium"
            data-testid="wizard-brand-name"
          >
            {brand.name}
          </span>
        </div>
        <div className="flex justify-center px-6 pb-3">
          <WizardProgress
            required_steps={required_steps}
            current_step={
              step === "welcome" ? (required_steps[0] ?? "tiktok") : step
            }
          />
        </div>
      </header>

      {/*
        OKS-304 — Optional full-bleed cover slot. Sits BELOW the sticky
        header (it scrolls with content, not pinned) and ABOVE the
        centered `<main>`. Welcome step passes the brand cover band;
        other steps pass nothing (slot is unrendered when undefined).
      */}
      {cover ? (
        <div data-testid="wizard-cover" className="w-full">
          {cover}
        </div>
      ) : null}

      <main
        role="main"
        tabIndex={-1}
        data-testid="wizard-main"
        className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 py-6 outline-none"
      >
        {children}
      </main>

      {/*
        Switcher chip — visually top-right via `fixed` positioning, but
        DOM-ordered AFTER <main> so keyboard users complete the step
        (Tab through the form, hit the primary CTA) BEFORE reaching the
        switcher. UX Spec §3 + §7 tab-order rule.

        The fixed-positioned chip is rendered below <main> (last child of
        the shell), so document tab order = heading → form fields → CTA →
        chip. This satisfies "DOM order: chip after CTA" without
        `aria-flowto` (DOM-fragile across browsers per WAI-ARIA caveats).
      */}
      {showSwitcher && (
        <div
          data-testid="wizard-switcher-slot"
          // z-30 floats the chip ABOVE the sticky header (z-20). Without
          // it the opaque `bg-background` on the header occludes the
          // chip in the same y-band (B7 manual-check regression). JSDOM
          // doesn't simulate layout, so this is also asserted in Vitest
          // and guarded by a Playwright `toBeVisible` canary on E4.
          className="fixed top-3 right-4 z-30"
        >
          <CrewSwitcher />
        </div>
      )}
    </div>
  )
}

export default WizardShell
